import React from 'react'
import { Button, Container, Loader, Spacer, Text } from 'roadie-ui'
import { Container as Framework, QuizModule, RequiredQuiz, Slide, Video } from '../framework'
import Oops from 'components/pages/certifications/Oops'
import useDriverCert from 'components/pages/certifications/useDriverCert'
import { TRAINING_CERTS } from 'util/constants'
import bannerImage from 'images/training/pharmacy/banner.png'
import bannerQuizImage from 'images/training/banner-quiz.png'
import bannerPassImage from 'images/training/pharmacy/banner-pass.png'
import bannerFailImage from 'images/training/pharmacy/banner-fail.png'
import videoThumb1 from 'images/training/pharmacy/01-video-thumbnail.png'
import videoThumb2 from 'images/training/pharmacy/02-video-thumbnail.png'
import videoThumb3 from 'images/training/pharmacy/03-video-thumbnail.png'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

//TODO: Combine with other VimeoVideo components
const VimeoVideo: React.FC<{
  id: string
  h: string
  thumbnail: string
}> = ({ id, h, thumbnail }) => {
  const { t } = useLocalization()

  return (
    <Video
      src={`https://player.vimeo.com/video/${id}?h=${h}&autoplay=0&muted=0&autopause=0&controls=1&playsinline=true&loop=0`}
      autoplayWhenVisible={false}
      showVideoInBackground={true}
      height="496"
      thumbnail={<img src={thumbnail} alt={t('dweb_common_video_thumbnail')} />}
    />
  )
}
const PharmacyPage: React.FC = () => {
  const { t } = useLocalization()
  const driverCertResult = useDriverCert(TRAINING_CERTS.PHARMACY.CERT)

  if (driverCertResult.loading) {
    return (
      <Container.Base $color="neutral-black">
        <Framework title={t('dweb_certifications_pharmacy_pharmacy_pharmacy_gigs')}>
          <Loader />
        </Framework>
      </Container.Base>
    )
  }

  if (driverCertResult.isDriverCertRevoked) {
    return (
      <Container.Base $color="neutral-black">
        <Framework title={t('dweb_certifications_pharmacy_pharmacy_pharmacy_gigs')}>
          <Oops message={t('dweb_certifications_pharmacy_revoked_ineligible_message')} />
        </Framework>
      </Container.Base>
    )
  }

  return (
    <Container.Base $color="neutral-black">
      <Framework title={t('dweb_certifications_pharmacy_pharmacy_pharmacy_gigs')}>
        <QuizModule certification={TRAINING_CERTS.PHARMACY.CERT} minimumScoreToPass={9}>
          {{
            slides: (
              <>
                <Slide>
                  <img
                    src={bannerImage}
                    alt={t('dweb_certifications_pharmacy_pharmacy_pharmacy_gig_certification_banner')}
                  />
                  <Spacer $height="2.4rem" />
                  <Text.Overline $color="primary-base">
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_start_delivering_pharmacy_gigs" />
                  </Text.Overline>
                  <Spacer $height="0.4rem" />
                  <Text.H4>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_pharmacy_gig_certification" />
                  </Text.H4>
                  <Spacer $height="0.4rem" />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_intro_description" />
                  </Text.Body2>
                  <br />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_training_goal" />
                  </Text.Body2>
                  <br />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_practices_completion_intro" />
                  </Text.Body2>
                </Slide>
                <Slide>
                  <Text.H5 $fontWeight="600">
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_getting_started" />
                  </Text.H5>
                  <Spacer $height="0.8rem" />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_best_practices_intro" />
                  </Text.Body2>
                  <Spacer $height="3.2rem" />
                  <VimeoVideo id="867198806" h="8154ec690e" thumbnail={videoThumb1} />
                </Slide>
                <Slide>
                  <Text.H5 $fontWeight="600">
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_delivery_signature" />
                  </Text.H5>
                  <Spacer $height="0.8rem" />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_delivery_process_intro" />
                  </Text.Body2>
                  <Spacer $height="3.2rem" />
                  <VimeoVideo id="867203057" h="b25b2082a5" thumbnail={videoThumb2} />
                </Slide>
                <Slide>
                  <Text.H5 $fontWeight="600">
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_returns_support" />
                  </Text.H5>
                  <Spacer $height="0.8rem" />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_returns_segment_intro" />
                  </Text.Body2>
                  <Spacer $height="3.2rem" />
                  <VimeoVideo id="867206942" h="2e9c134757" thumbnail={videoThumb3} />
                </Slide>
                <Slide>
                  <img src={bannerQuizImage} alt={t('dweb_certifications_pharmacy_pharmacy_show_what_know_banner')} />
                  <Spacer $height="3.2rem" />
                  <Text.H5 $fontWeight="600">
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_show_what_know" />
                  </Text.H5>
                  <Spacer $height="0.8rem" />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_quiz_intro" />
                  </Text.Body2>
                </Slide>
              </>
            ),
            questions: (
              <>
                <Slide>
                  <RequiredQuiz
                    questionNumber={1}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q1_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q1_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q1_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q1_option_2',
                        value: '2',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q1_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q1_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={2}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q2_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q2_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q2_option_1',
                        value: '1',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q2_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q2_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q2_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={3}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q3_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q3_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q3_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q3_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q3_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q3_option_4',
                        value: '4',
                        isCorrect: true,
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={4}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q4_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q4_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q4_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q4_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q4_option_3',
                        value: '3',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q4_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={5}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q5_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q5_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q5_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q5_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q5_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q5_option_4',
                        value: '4',
                        isCorrect: true,
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={6}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q6_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q6_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q6_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q6_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q6_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q6_option_4',
                        value: '4',
                        isCorrect: true,
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={7}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q7_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q7_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q7_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q7_option_2',
                        value: '2',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q7_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q7_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={8}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q8_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q8_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q8_option_1',
                        value: '1',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q8_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q8_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q8_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={9}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q9_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q9_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q9_option_1',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q9_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q9_option_3',
                        value: '3',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q9_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={10}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q10_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q10_description"
                    options={[
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q10_option_1',
                        value: '1',
                        isCorrect: true,
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q10_option_2',
                        value: '2',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q10_option_3',
                        value: '3',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q10_option_4',
                        value: '4',
                      },
                    ]}
                  />
                </Slide>
                <Slide>
                  <RequiredQuiz
                    questionNumber={11}
                    totalQuestions={11}
                    questionKey="dweb_certifications_pharmacy_quiz_q11_question"
                    descriptionKey="dweb_certifications_pharmacy_quiz_q11_description"
                    options={[
                      {
                        textKey: 'dweb_common_yes',
                        value: '1',
                      },
                      {
                        textKey: 'dweb_certifications_pharmacy_quiz_q11_option_2',
                        value: '2',
                        isCorrect: true,
                      },
                    ]}
                  />
                </Slide>
              </>
            ),
            resultSlide: (results: { passed: boolean; numCorrectAnswers: number; resetModule: VoidFunction }) =>
              results.passed ? (
                <Slide>
                  <img src={bannerPassImage} alt={t('dweb_certifications_pharmacy_alt_passed_banner')} />
                  <Spacer $height="3.2rem" />
                  <Text.H4>
                    <LocalizedText translationKey="dweb_certifications_mail_innovations_great_job" />
                  </Text.H4>
                  <Spacer $height="0.8rem" />
                  <Text.Body2 $fontWeight="bold">
                    <LocalizedRichText
                      translationKey="dweb_certifications_pharmacy_pharmacy_got_value1_out_11_questions_correct"
                      params={{
                        value1: results.numCorrectAnswers,
                      }}
                    />
                  </Text.Body2>
                  <br />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_completion_message" />
                  </Text.Body2>
                  <br />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_guidelines_success" />
                  </Text.Body2>
                  <br />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_see_road" />
                  </Text.Body2>
                </Slide>
              ) : (
                <Slide>
                  <img src={bannerFailImage} alt={t('dweb_common_failed_banner')} />
                  <Spacer $height="3.2rem" />
                  <Text.H4>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_oh_no" />
                  </Text.H4>
                  <Spacer $height="0.8rem" />
                  <Text.Body2 $fontWeight="bold">
                    <LocalizedRichText
                      translationKey="dweb_certifications_pharmacy_pharmacy_only_got_value1_out_11_correct"
                      params={{
                        value1: results.numCorrectAnswers,
                      }}
                    />
                  </Text.Body2>
                  <br />
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_pharmacy_quiz_retry_message" />
                  </Text.Body2>
                  <Spacer $height="4.0rem" />
                  <Button.Primary onClick={results.resetModule} $width="100%">
                    <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_start_over" />
                  </Button.Primary>
                </Slide>
              ),
          }}
        </QuizModule>
      </Framework>
    </Container.Base>
  )
}
export default PharmacyPage
